import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent } from 'react';

import { useCaptureError } from '@/lib/posthog/hooks/useCaptureError';
import { useSignalRConnection } from '@/lib/signalR/useSignalRConnection';
import { candidateJobQueryKeys } from '@/modules/candidates/queries/queryKeys';
import { chatQueryKeys } from '@/modules/hiring-manager/chat/queryKeys';
import { jobsQueryKeys } from '@/modules/jobs/queries/queryKeys';
import { messagingQueryKeys } from '@/modules/messaging/queries/queryKeys';
import { NOTIFICATION_HUB_URL } from '@/modules/notifications/constants';
import { notificationsQueryKeys } from '@/modules/notifications/queries/queryKeys';
import type {
  EntityChangePayload,
  NotificationPayload,
} from '@/modules/notifications/types';
import { createKeyedThrottle } from '@/modules/notifications/utils/createKeyedThrottle';
import {
  invalidateEntityChange,
  invalidateEntityQueries,
} from '@/modules/notifications/utils/invalidateEntityQueries';

const ENTITY_CHANGE_THROTTLE_MS = 2000;

/**
 * Listens to the notification hub: every notification refreshes the
 * notification list and whatever entity it says changed, and every entity
 * change (candidates sourced or moved between stages, Finlay's report in a
 * chat) refreshes that data without a notification. Mount once per app shell
 * (the recruiter header, the portal top bar).
 */
export const useNotificationHub = (
  onNotification?: (payload: NotificationPayload) => void,
) => {
  const queryClient = useQueryClient();
  const captureError = useCaptureError();
  const connection = useSignalRConnection({ hubUrl: NOTIFICATION_HUB_URL });

  const handleNotification = useEffectEvent((payload: NotificationPayload) => {
    try {
      void queryClient.invalidateQueries({
        queryKey: notificationsQueryKeys.list(),
      });
      if (payload.entityAction) {
        invalidateEntityQueries(
          queryClient,
          payload.entityAction,
          payload.entityId,
        );
      }
      onNotification?.(payload);
    } catch (error) {
      captureError(error, {
        errorType: 'notifications-handle-notification-failed',
        notificationId: payload.id,
        entityAction: payload.entityAction || 'none',
        entityId: payload.entityId || 'none',
      });
    }
  });

  // The server pushes each notification once; anything sent while the hub
  // was reconnecting only shows up by refetching.
  const refetchAfterReconnect = useEffectEvent(() => {
    for (const queryKey of [
      notificationsQueryKeys.all,
      jobsQueryKeys.all,
      candidateJobQueryKeys.all,
      messagingQueryKeys.all,
      chatQueryKeys.all,
    ]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  });

  const handleCandidateJobsChange = useEffectEvent((jobId: string) => {
    try {
      invalidateEntityChange(queryClient, { entity: 'CandidateJobs', jobId });
    } catch (error) {
      captureError(error, {
        errorType: 'notifications-handle-entity-change-failed',
        entity: 'CandidateJobs',
        jobId,
      });
    }
  });

  const handleChatChange = useEffectEvent((chatId: string) => {
    try {
      invalidateEntityChange(queryClient, { entity: 'Chat', chatId });
    } catch (error) {
      captureError(error, {
        errorType: 'notifications-handle-entity-change-failed',
        entity: 'Chat',
        chatId,
      });
    }
  });

  useEffect(() => {
    if (!connection) return;
    let isActive = true;
    // Sourcing commits a batch every few seconds; spacing the refetches keeps
    // a long candidate list from restarting before it settles.
    const candidateJobsThrottle = createKeyedThrottle(
      (jobId) => handleCandidateJobsChange(jobId),
      ENTITY_CHANGE_THROTTLE_MS,
    );
    const handleEntityChange = (change: EntityChangePayload) => {
      if (change.entity === 'CandidateJobs') {
        candidateJobsThrottle.call(change.jobId);
      } else if (change.entity === 'Chat') {
        handleChatChange(change.chatId);
      }
    };
    connection.on('ReceiveNotification', handleNotification);
    connection.on('ReceiveEntityChange', handleEntityChange);
    // SignalR has no way to remove an onreconnected callback.
    connection.onreconnected(() => {
      if (isActive) refetchAfterReconnect();
    });
    return () => {
      isActive = false;
      connection.off('ReceiveNotification', handleNotification);
      connection.off('ReceiveEntityChange', handleEntityChange);
      candidateJobsThrottle.cancel();
    };
  }, [connection]);
};
